import { EmailActions, EmailAPI, EmailUtils } from '.';
import { AuthenticationActions, AuthenticationUtils } from '../base';
import { getAuthorizationFromToken } from './utils';

const logout = (reason) => (dispatch) => dispatch(AuthenticationActions.logout(reason));

// REFRESH

/**
 * Dispatches the success auth actions, and calls `UserActioner.retrieveAuthenticatedUserInfo`
 *
 * @param {Object} result - The result of the successful refresh
 * @param {String} result.access_token
 * @param {String} result.refresh_token
 * @param {Number} result.expires_in
 * @param {String} result.token_type
 * @param {String} result.expiration_date: - ms since epoch
 *
 * @returns {Promise<Object>} result - the result of the refresh, same as the login result
 */
const refreshSuccess = (result) => (dispatch) => {
    dispatch(AuthenticationActions.setAuthorizationHeader(getAuthorizationFromToken(result.access_token)));
    dispatch(AuthenticationActions.setAuthenticated(true));
    dispatch(EmailActions.refreshEmailSuccess(result));

    dispatch(AuthenticationActions.setInitialized(true, 'refresh success'));

    return Promise.resolve(result);
};

/**
 * Dispatches the email refresh failure
 *
 * @throws {Promise<Error>} the error received
 */
const refreshFail = (error) => (dispatch) => {
    dispatch(AuthenticationActions.clearAuthorizationHeader());
    dispatch(AuthenticationActions.setAuthenticated(false));
    dispatch(EmailActions.refreshEmailFailure(error));
    dispatch(AuthenticationActions.setInitialized(true, 'refresh fail'));

    return Promise.reject(error);
};

/**
 * If the access token is not expired, fires refreshSuccess right away with current state.
 * Resets the initialized state (keeps the authenticated state) and then refreshes the access token
 *
 * @param {Boolean} forceRefresh - If should refresh the tokens, even if the current ones are not expired
 *
 * @returns {Promise<Object>} result - the result of the refresh, same as the login result OR an object
 * with props `isError` and `err`
 * @throws {Promise<Error>} an Axios error if the refresh failed
 */
const refreshAuth = (forceRefresh) => (dispatch, getState) => {
    const { expiration_date } = getState().Authentication;

    if (!AuthenticationUtils.authenticationIsExpired(expiration_date) && !forceRefresh) {
        const res = {
            access_token: getState().Authentication.access_token,
            refresh_token: getState().Authentication.refresh_token,
            expires_in: getState().Authentication.expires_in,
            token_type: getState().Authentication.token_type,
            expiration_date: getState().Authentication.expiration_date,
        };

        return dispatch(refreshSuccess(res));
    }

    const { refresh_token } = getState().Authentication;

    if (!refresh_token) {
        return dispatch(refreshFail(new Error('No refresh token found, cannot refresh and will logout'))).catch(
            (err) => ({ isError: true, err }),
        );
    }

    dispatch(AuthenticationActions.setInitialized(false, 'loading refresh')); // is loading

    return EmailAPI.refresh(refresh_token, dispatch)
        .then((res) => {
            const transformedAuth = EmailUtils.transformAuth(res.data);
            return dispatch(refreshSuccess(transformedAuth));
        })
        .catch((err) => dispatch(refreshFail(err)));
};

// LOGIN

/**
 * Dispatches the success auth actions, and calls `UserActioner.retrieveAuthenticatedUserInfo`
 *
 * @param {Object} result - The result of the successful login
 * @param {String} result.access_token
 * @param {String} result.refersh_token
 * @param {Number} result.expires_in
 * @param {String} result.token_type
 * @param {String} result.expiration_date: - ms since epoch
 *
 * @returns {Promise<Object>} result - the result received
 */
const loginSuccess = (auth) => (dispatch) => {
    dispatch(AuthenticationActions.setAuthorizationHeader(getAuthorizationFromToken(auth.access_token)));
    dispatch(AuthenticationActions.setAuthenticated(true));
    dispatch(EmailActions.loginEmailSuccess(auth));
    dispatch(AuthenticationActions.setInitialized(true, 'login success'));

    return Promise.resolve(auth);
};

/**
 * Dispatches the email login failure with the errror received
 *
 * @param {Error} error
 * @throws {Promise<Error>} the error received
 */
const loginFail = (error) => (dispatch) => {
    dispatch(AuthenticationActions.clearAuthorizationHeader());
    dispatch(AuthenticationActions.setAuthenticated(false));
    dispatch(EmailActions.loginEmailFailure(error));
    dispatch(AuthenticationActions.setInitialized(true, 'login fail'));

    return Promise.reject(error);
};

/**
 *
 * @param {String} email
 * @param {String} password
 *
 * @returns {Promise<Object>} the full result of the login: access_token, refresh_token...
 * @throws {Promise<Error>} an Axios error if the request failed
 */
const login = (email, password) => (dispatch) => EmailAPI.login(email, password, dispatch, false)
    .then((res) => {
        const transformedAuth = EmailUtils.transformAuth(res.data);
        return dispatch(loginSuccess(transformedAuth));
    })
    .catch((err) => dispatch(loginFail(err)));

// TOKEN

/**
 * Automatically refreshes the access token if necessary
 *
 * @returns {Promise<String>} the full authorization header. Ex: `Bearer abcedf`
 * @throws {Promise<Error>} an Axios error if the refresh failed
 */
const getAuthorization = () => (dispatch, getState) => {
    const { expiration_date } = getState().Authentication;

    if (AuthenticationUtils.authenticationIsExpired(expiration_date)) {
        return dispatch(refreshAuth()).then((auth) => EmailUtils.getAuthorizationFromToken(auth.access_token));
    }

    const { access_token } = getState().Authentication;
    const auth = EmailUtils.getAuthorizationFromToken(access_token);

    return Promise.resolve(auth);
};

export {
    loginSuccess, loginFail, login, getAuthorization, refreshAuth, logout,
};
